// @givmo/board — a director page that meets "You no longer have access to this board." (CBG-FIX34;
// frames f3 and f3b, approved for building 2026-10-13 22:40 PDT).
//
// The backend re-reads the seat on every request and refuses a seat whose access ended — recorded as
// former, released, moved, never claimed — with ONE undifferentiated code (`accessDenied`), and `/me`
// lists only the seats this account holds now. So whatever meets that refusal, an act or a read,
// reads `/me` again: a board it no longer lists moves the page to its no-seat state
// (`DirectorBoardContent`), which says the refusal's sentence and the way back to the account's
// boards, with nothing to sign left on the page.
//
//   * AN ACT (`useDirectorAct`) holds Sign pending through the re-read, then says the sentence where
//     the act was; a page whose board is gone has moved by then.
//   * A READ shows nothing for the refused read while `/me` is read again, so no "Try again" flashes,
//     and never "Try again" after it: trying the refused read again can never succeed. If `/me` still
//     lists the board (a refusal the next read contradicts), the sentence stays, on its own.
//   * ANY OTHER REQUEST a page makes (an RSVP, a note, a download) says the sentence as it does today,
//     and reads `/me` again too (`useDirectorFailureSentence`).
//   * A NOT-FOUND READ (CBG-FIX91, frame o13) says the page's own line with no "Try again": a meeting,
//     packet, record or consent this seat can't open — a link to something of another body among
//     them, which answers exactly as an id that doesn't exist — can never open on a retry.
//
// THE SENTENCE FOLLOWS THE SEAT `/me` LISTED (CBG-FIX104, row 4.1): a seat this account's own `/me`
// listed as a committee's, for someone who isn't on the board, is told "You no longer have access to
// this committee." (or "these committees"), never "this board", which it never had. The backend's
// refusal is one sentence for every cause and no page shows it.

import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isGivmoError } from '@givmo/api-client';
import { Button, Card, CardBody, testIdProp } from '@givmo/design-system';
import { useSearch } from '../board-routing';
import { directorOrgOf } from './director-board';
import { boardDirectorMeKey, useDirectorMe, useListedSeat } from './director-me';
import {
  directorFailureSentence,
  isAccessDenied,
  noLongerHasAccess,
  type ListedSeat,
} from './director-wire';

/**
 * CBG-FIX104 (row 4.1): the seat `/me` last listed on the board this page is about (`?org=`), so
 * the access refusal says "this committee" to a seat it listed as a committee's — remembered, since
 * by the time the page says it, `/me` may no longer list the seat at all.
 */
export function useListedSeatHere(): ListedSeat | null {
  return useListedSeat(directorOrgOf(useSearch()));
}

/** CBG-FIX104 (row 4.1): the access refusal's sentence on this page, for the seat listed here. */
export function useNoLongerHasAccess(): string {
  return noLongerHasAccess(useListedSeatHere());
}

/**
 * `directorFailureSentence` on this page: the access refusal in the words of the seat `/me` listed
 * here (CBG-FIX104). It reads `/me` again for nothing — an act has already asked for that;
 * `useDirectorFailureSentence` adds the re-read for any other request.
 */
export function useDirectorFailureWords(): (caught: unknown, fallback: string) => string {
  const listed = useListedSeatHere();
  return (caught, fallback) => directorFailureSentence(caught, fallback, listed);
}

interface FailedRead {
  isError: boolean;
  error: unknown;
  refetch: () => Promise<unknown>;
}

/**
 * Whether a read met the access refusal, and whether `/me` has yet to answer the re-read it asked for.
 * `/me` is read again once for each refusal; until it answers (or fails), the refusal is "rereading".
 */
export function useAccessRefusal(read: { isError: boolean; error: unknown }): {
  refused: boolean;
  rereading: boolean;
} {
  const queryClient = useQueryClient();
  const meQuery = useDirectorMe();
  const refusal = read.isError && isAccessDenied(read.error) ? read.error : null;
  const [asked, setAsked] = useState<{ refusal: unknown; at: number } | null>(null);
  useEffect(() => {
    if (refusal === null || asked?.refusal === refusal) return;
    setAsked({ refusal, at: Date.now() });
    void queryClient.invalidateQueries({ queryKey: boardDirectorMeKey() });
  }, [refusal, asked, queryClient]);
  const answered =
    asked !== null &&
    asked.refusal === refusal &&
    Math.max(meQuery.dataUpdatedAt, meQuery.errorUpdatedAt) >= asked.at;
  return { refused: refusal !== null, rereading: refusal !== null && !answered };
}

/**
 * A failed request's sentence, as `directorFailureSentence` says it; and, when the board refused this
 * account, `/me` read again, so a page whose board is gone moves to its no-seat state.
 */
export function useDirectorFailureSentence(): (caught: unknown, fallback: string) => string {
  const queryClient = useQueryClient();
  const words = useDirectorFailureWords();
  return (caught, fallback) => {
    if (isAccessDenied(caught)) {
      void queryClient.invalidateQueries({ queryKey: boardDirectorMeKey() });
    }
    return words(caught, fallback);
  };
}

/**
 * A board page's failed read: its own line and "Try again" — never for a not-found, which a retry can
 * never change (CBG-FIX91); or, for the access refusal, nothing while `/me` is read again, then the
 * refusal's sentence on its own.
 */
export function DirectorReadFailed({
  read,
  line,
  testId,
  retryTestId,
  lineClassName = 'font-sans text-s15 font-w400 leading-h15 tracking-givmo text-text-secondary',
}: {
  read: FailedRead;
  /** The page's own words for a read that failed for any other reason. */
  line: string;
  testId: string;
  retryTestId: string;
  lineClassName?: string;
}) {
  const { refused, rereading } = useAccessRefusal(read);
  const noLongerHere = useNoLongerHasAccess();
  const notFound = read.isError && isGivmoError(read.error) && read.error.status === 404;
  if (rereading) return null;
  return (
    <Card testId={testId}>
      <CardBody className="flex flex-col items-start gap-12">
        <p className={lineClassName} {...testIdProp(`${testId}-line`)}>
          {refused ? noLongerHere : line}
        </p>
        {refused || notFound ? null : (
          <Button
            appearance="saas"
            tone="secondary"
            onClick={() => void read.refetch()}
            testId={retryTestId}
          >
            Try again
          </Button>
        )}
      </CardBody>
    </Card>
  );
}
